/**
 * D'Voure Fine Jewelry - Main Theme JavaScript
 * Vanilla JS - No external dependencies
 */

(function () {
  'use strict';

  /* ============================================
   * MEGA MENU
   * ============================================ */
  const MegaMenu = {
    init() {
      const menuItems = document.querySelectorAll('[data-mega-menu-trigger]');
      if (!menuItems.length) return;

      let closeTimeout = null;

      menuItems.forEach((trigger) => {
        const menuId = trigger.getAttribute('data-mega-menu-trigger');
        const dropdown = document.querySelector(`[data-mega-menu="${menuId}"]`);
        if (!dropdown) return;

        trigger.addEventListener('mouseenter', () => {
          clearTimeout(closeTimeout);
          this.closeAll();
          dropdown.classList.add('is-active');
          trigger.setAttribute('aria-expanded', 'true');
        });

        trigger.addEventListener('mouseleave', () => {
          closeTimeout = setTimeout(() => {
            dropdown.classList.remove('is-active');
            trigger.setAttribute('aria-expanded', 'false');
          }, 300);
        });

        dropdown.addEventListener('mouseenter', () => {
          clearTimeout(closeTimeout);
        });

        dropdown.addEventListener('mouseleave', () => {
          closeTimeout = setTimeout(() => {
            dropdown.classList.remove('is-active');
            trigger.setAttribute('aria-expanded', 'false');
          }, 300);
        });

        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          const isActive = dropdown.classList.contains('is-active');
          this.closeAll();
          if (!isActive) {
            dropdown.classList.add('is-active');
            trigger.setAttribute('aria-expanded', 'true');
          }
        });
      });

      document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-mega-menu-trigger]') && !e.target.closest('[data-mega-menu]')) {
          this.closeAll();
        }
      });
    },

    closeAll() {
      document.querySelectorAll('[data-mega-menu]').forEach((menu) => {
        menu.classList.remove('is-active');
      });
      document.querySelectorAll('[data-mega-menu-trigger]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
    }
  };

  /* ============================================
   * CART DRAWER
   * ============================================ */
  const CartDrawer = {
    drawer: null,
    overlay: null,

    init() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.overlay = document.querySelector('[data-cart-drawer-overlay]');
      if (!this.drawer) return;

      document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      const closeBtn = this.drawer.querySelector('[data-cart-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.close());
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this.close();
        }
      });

      this.drawer.addEventListener('click', (e) => {
        const quantityBtn = e.target.closest('[data-cart-quantity]');
        if (quantityBtn) {
          const key = quantityBtn.getAttribute('data-cart-item-key');
          const action = quantityBtn.getAttribute('data-cart-quantity');
          const input = this.drawer.querySelector(`[data-cart-quantity-input][data-cart-item-key="${key}"]`);
          let quantity = input ? parseInt(input.value, 10) : 1;

          if (action === 'minus') quantity = Math.max(0, quantity - 1);
          if (action === 'plus') quantity += 1;

          this.updateItem(key, quantity);
        }

        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          const key = removeBtn.getAttribute('data-cart-item-key');
          this.updateItem(key, 0);
        }
      });

      document.addEventListener('cart:add', (e) => {
        this.addItem(e.detail);
      });

      this.refreshCart();
    },

    open() {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    },

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-visible');
      document.body.style.overflow = '';
    },

    async addItem(data) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });

        if (!response.ok) throw new Error('Failed to add item');

        await this.refreshCart();
        this.open();
      } catch (error) {
        console.error('Cart add error:', error);
      }
    },

    async updateItem(key, quantity) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });

        if (!response.ok) throw new Error('Failed to update item');

        await this.refreshCart();
      } catch (error) {
        console.error('Cart update error:', error);
      }
    },

    async refreshCart() {
      try {
        const response = await fetch('/cart.js', {
          headers: { 'Accept': 'application/json' }
        });
        const cart = await response.json();

        this.renderCart(cart);
        this.updateCounter(cart.item_count);
      } catch (error) {
        console.error('Cart refresh error:', error);
      }
    },

    renderCart(cart) {
      const itemsContainer = this.drawer?.querySelector('[data-cart-items]');
      const subtotalEl = this.drawer?.querySelector('[data-cart-subtotal]');
      const emptyMsg = this.drawer?.querySelector('[data-cart-empty]');
      const footer = this.drawer?.querySelector('[data-cart-footer]');

      if (!itemsContainer) return;

      if (cart.item_count === 0) {
        itemsContainer.innerHTML = '';
        if (emptyMsg) emptyMsg.style.display = 'block';
        if (footer) footer.style.display = 'none';
        return;
      }

      if (emptyMsg) emptyMsg.style.display = 'none';
      if (footer) footer.style.display = 'block';

      itemsContainer.innerHTML = cart.items.map((item) => `
        <div class="cart-drawer__item" data-cart-item="${item.key}">
          <div class="cart-drawer__item-image">
            <img src="${item.image}" alt="${item.title}" loading="lazy" width="80" height="80">
          </div>
          <div class="cart-drawer__item-details">
            <h4 class="cart-drawer__item-title">${item.product_title}</h4>
            ${item.variant_title ? `<p class="cart-drawer__item-variant">${item.variant_title}</p>` : ''}
            <p class="cart-drawer__item-price">${this.formatMoney(item.final_line_price)}</p>
            <div class="cart-drawer__item-quantity">
              <button type="button" data-cart-quantity="minus" data-cart-item-key="${item.key}" aria-label="Decrease quantity">-</button>
              <input type="number" value="${item.quantity}" min="0" data-cart-quantity-input data-cart-item-key="${item.key}" aria-label="Quantity" readonly>
              <button type="button" data-cart-quantity="plus" data-cart-item-key="${item.key}" aria-label="Increase quantity">+</button>
            </div>
          </div>
          <button type="button" class="cart-drawer__item-remove" data-cart-remove data-cart-item-key="${item.key}" aria-label="Remove item">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.5"/></svg>
          </button>
        </div>
      `).join('');

      if (subtotalEl) {
        subtotalEl.textContent = this.formatMoney(cart.total_price);
      }
    },

    updateCounter(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.style.display = count > 0 ? '' : 'none';
      });
    },

    formatMoney(cents) {
      return (cents / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      });
    }
  };

  /* ============================================
   * SCROLL ANIMATIONS
   * ============================================ */
  const ScrollAnimations = {
    init() {
      const elements = document.querySelectorAll('.animate-on-scroll');
      if (!elements.length) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry, index) => {
            if (entry.isIntersecting) {
              const delay = entry.target.dataset.animationDelay || index * 100;
              setTimeout(() => {
                entry.target.classList.add('animated');
              }, delay);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      );

      elements.forEach((el) => observer.observe(el));
    }
  };

  /* ============================================
   * STICKY HEADER
   * ============================================ */
  const StickyHeader = {
    init() {
      const header = document.querySelector('[data-header]');
      if (!header) return;

      let lastScroll = 0;
      const threshold = 100;

      const handleScroll = () => {
        const currentScroll = window.scrollY;

        if (currentScroll > threshold) {
          header.classList.add('header--scrolled');
        } else {
          header.classList.remove('header--scrolled');
        }

        if (currentScroll > lastScroll && currentScroll > threshold * 2) {
          header.classList.add('header--hidden');
        } else {
          header.classList.remove('header--hidden');
        }

        lastScroll = currentScroll;
      };

      let ticking = false;
      window.addEventListener('scroll', () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            handleScroll();
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });
    }
  };

  /* ============================================
   * MOBILE MENU
   * ============================================ */
  const MobileMenu = {
    menu: null,
    overlay: null,

    init() {
      this.menu = document.querySelector('[data-mobile-menu]');
      this.overlay = document.querySelector('[data-mobile-menu-overlay]');
      if (!this.menu) return;

      document.querySelectorAll('[data-mobile-menu-toggle]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggle();
        });
      });

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      this.menu.querySelectorAll('[data-mobile-submenu-toggle]').forEach((trigger) => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          const submenu = trigger.nextElementSibling;
          if (submenu) {
            trigger.classList.toggle('is-open');
            submenu.classList.toggle('is-open');
          }
        });
      });
    },

    toggle() {
      if (this.menu.classList.contains('is-open')) {
        this.close();
      } else {
        this.open();
      }
    },

    open() {
      this.menu.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';

      const toggleBtn = document.querySelector('[data-mobile-menu-toggle]');
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'true');
    },

    close() {
      this.menu.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-visible');
      document.body.style.overflow = '';

      const toggleBtn = document.querySelector('[data-mobile-menu-toggle]');
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
    }
  };

  /* ============================================
   * PRODUCT IMAGE GALLERY
   * ============================================ */
  const ProductGallery = {
    init() {
      const gallery = document.querySelector('[data-product-gallery]');
      if (!gallery) return;

      const mainImage = gallery.querySelector('[data-product-main-image]');
      const thumbnails = gallery.querySelectorAll('[data-product-thumbnail]');
      const zoomContainer = gallery.querySelector('[data-product-zoom]');

      if (!mainImage) return;

      thumbnails.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const src = thumb.getAttribute('data-product-thumbnail');
          const srcset = thumb.getAttribute('data-srcset') || '';
          mainImage.src = src;
          if (srcset) mainImage.srcset = srcset;

          thumbnails.forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        });
      });

      if (zoomContainer) {
        zoomContainer.addEventListener('mousemove', (e) => {
          const rect = zoomContainer.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;

          mainImage.style.transformOrigin = `${x}% ${y}%`;
          mainImage.style.transform = 'scale(2)';
        });

        zoomContainer.addEventListener('mouseleave', () => {
          mainImage.style.transform = 'scale(1)';
          mainImage.style.transformOrigin = 'center center';
        });
      }
    }
  };

  /* ============================================
   * VARIANT SELECTOR
   * ============================================ */
  const VariantSelector = {
    init() {
      const selectors = document.querySelectorAll('[data-variant-selector]');
      if (!selectors.length) return;

      selectors.forEach((selector) => {
        const productData = selector.closest('[data-product-json]');
        if (!productData) return;

        let product;
        try {
          product = JSON.parse(productData.textContent);
        } catch (e) {
          return;
        }

        const options = selector.querySelectorAll('[data-variant-option]');
        options.forEach((option) => {
          option.addEventListener('change', () => {
            this.onVariantChange(selector, product);
          });
        });
      });
    },

    onVariantChange(selector, product) {
      const selectedOptions = [];
      selector.querySelectorAll('[data-variant-option]').forEach((option) => {
        if (option.type === 'radio') {
          if (option.checked) selectedOptions.push(option.value);
        } else {
          selectedOptions.push(option.value);
        }
      });

      const variant = product.variants.find((v) =>
        v.options.every((opt, i) => opt === selectedOptions[i])
      );

      if (!variant) return;

      this.updatePrice(selector, variant);
      this.updateImage(variant);
      this.updateAvailability(selector, variant);
      this.updateURL(variant);
      this.updateAddToCart(selector, variant);
    },

    updatePrice(selector, variant) {
      const container = selector.closest('[data-product]') || document;
      const priceEl = container.querySelector('[data-product-price]');
      const comparePriceEl = container.querySelector('[data-product-compare-price]');

      if (priceEl) {
        priceEl.textContent = (variant.price / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD'
        });
      }

      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = (variant.compare_at_price / 100).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD'
          });
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }
    },

    updateImage(variant) {
      if (!variant.featured_image) return;

      const mainImage = document.querySelector('[data-product-main-image]');
      if (mainImage) {
        mainImage.src = variant.featured_image.src;
      }

      const thumbnail = document.querySelector(`[data-product-thumbnail="${variant.featured_image.src}"]`);
      if (thumbnail) {
        thumbnail.click();
      }
    },

    updateAvailability(selector, variant) {
      const container = selector.closest('[data-product]') || document;
      const badge = container.querySelector('[data-availability-badge]');

      if (badge) {
        badge.textContent = variant.available ? '' : (badge.getAttribute('data-sold-out-text') || 'Sold Out');
        badge.style.display = variant.available ? 'none' : '';
      }
    },

    updateURL(variant) {
      if (!variant.id) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    },

    updateAddToCart(selector, variant) {
      const container = selector.closest('[data-product]') || document;
      const addBtn = container.querySelector('[data-add-to-cart]');
      const variantInput = container.querySelector('input[name="id"]');

      if (variantInput) {
        variantInput.value = variant.id;
      }

      if (addBtn) {
        addBtn.disabled = !variant.available;
        const btnText = addBtn.querySelector('[data-add-to-cart-text]');
        if (btnText) {
          btnText.textContent = variant.available
            ? (btnText.getAttribute('data-default-text') || 'Add to Cart')
            : (btnText.getAttribute('data-sold-out-text') || 'Sold Out');
        }
      }
    }
  };

  /* ============================================
   * ACCORDION
   * ============================================ */
  const Accordion = {
    init() {
      document.querySelectorAll('[data-accordion]').forEach((accordion) => {
        const triggers = accordion.querySelectorAll('[data-accordion-trigger]');

        triggers.forEach((trigger) => {
          trigger.addEventListener('click', () => {
            const content = trigger.nextElementSibling;
            if (!content) return;

            const isOpen = trigger.classList.contains('is-open');

            if (accordion.hasAttribute('data-accordion-single')) {
              triggers.forEach((t) => {
                t.classList.remove('is-open');
                t.setAttribute('aria-expanded', 'false');
                const c = t.nextElementSibling;
                if (c) {
                  c.style.maxHeight = '0';
                  c.classList.remove('is-open');
                }
              });
            }

            if (!isOpen) {
              trigger.classList.add('is-open');
              trigger.setAttribute('aria-expanded', 'true');
              content.classList.add('is-open');
              content.style.maxHeight = content.scrollHeight + 'px';
            } else {
              trigger.classList.remove('is-open');
              trigger.setAttribute('aria-expanded', 'false');
              content.style.maxHeight = '0';
              content.classList.remove('is-open');
            }
          });
        });
      });
    }
  };

  /* ============================================
   * ANNOUNCEMENT BAR
   * ============================================ */
  const AnnouncementBar = {
    init() {
      const bar = document.querySelector('[data-announcement-bar]');
      if (!bar) return;

      const barId = bar.getAttribute('data-announcement-bar') || 'default';
      const dismissKey = `dvoure-announcement-dismissed-${barId}`;

      if (localStorage.getItem(dismissKey) === 'true') {
        bar.remove();
        return;
      }

      const dismissBtn = bar.querySelector('[data-announcement-dismiss]');
      if (dismissBtn) {
        dismissBtn.addEventListener('click', () => {
          bar.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
          bar.style.opacity = '0';
          bar.style.maxHeight = '0';
          bar.style.overflow = 'hidden';
          setTimeout(() => bar.remove(), 300);
          localStorage.setItem(dismissKey, 'true');
        });
      }
    }
  };

  /* ============================================
   * NEWSLETTER FORM
   * ============================================ */
  const NewsletterForm = {
    init() {
      document.querySelectorAll('[data-newsletter-form]').forEach((form) => {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();

          const emailInput = form.querySelector('input[type="email"]');
          const submitBtn = form.querySelector('button[type="submit"]');
          const messageEl = form.querySelector('[data-newsletter-message]');

          if (!emailInput || !emailInput.value) return;

          if (submitBtn) submitBtn.disabled = true;

          try {
            const formData = new FormData(form);
            const response = await fetch(form.action, {
              method: 'POST',
              body: formData,
              headers: { 'Accept': 'application/json' }
            });

            if (messageEl) {
              if (response.ok) {
                messageEl.textContent = messageEl.getAttribute('data-success-message') || 'Thank you for subscribing!';
                messageEl.classList.add('is-success');
                messageEl.classList.remove('is-error');
                emailInput.value = '';
              } else {
                messageEl.textContent = messageEl.getAttribute('data-error-message') || 'Something went wrong. Please try again.';
                messageEl.classList.add('is-error');
                messageEl.classList.remove('is-success');
              }
              messageEl.style.display = 'block';
            }
          } catch (error) {
            if (messageEl) {
              messageEl.textContent = messageEl.getAttribute('data-error-message') || 'Something went wrong. Please try again.';
              messageEl.classList.add('is-error');
              messageEl.classList.remove('is-success');
              messageEl.style.display = 'block';
            }
          } finally {
            if (submitBtn) submitBtn.disabled = false;
          }
        });
      });
    }
  };

  /* ============================================
   * TESTIMONIALS CAROUSEL
   * ============================================ */
  const TestimonialsCarousel = {
    init() {
      document.querySelectorAll('[data-testimonials-carousel]').forEach((carousel) => {
        this.setup(carousel);
      });
    },

    setup(carousel) {
      const track = carousel.querySelector('[data-carousel-track]');
      const slides = carousel.querySelectorAll('[data-carousel-slide]');
      const dotsContainer = carousel.querySelector('[data-carousel-dots]');

      if (!track || slides.length < 2) return;

      let currentIndex = 0;
      let autoplayInterval = null;
      const autoplayDelay = parseInt(carousel.getAttribute('data-autoplay-delay'), 10) || 5000;

      if (dotsContainer) {
        slides.forEach((_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.classList.add('carousel-dot');
          if (i === 0) dot.classList.add('is-active');
          dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
          dot.addEventListener('click', () => goTo(i));
          dotsContainer.appendChild(dot);
        });
      }

      const dots = dotsContainer ? dotsContainer.querySelectorAll('.carousel-dot') : [];

      const goTo = (index) => {
        currentIndex = index;
        track.style.transform = `translateX(-${currentIndex * 100}%)`;
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === currentIndex);
        });
      };

      const next = () => {
        goTo((currentIndex + 1) % slides.length);
      };

      const startAutoplay = () => {
        autoplayInterval = setInterval(next, autoplayDelay);
      };

      const stopAutoplay = () => {
        clearInterval(autoplayInterval);
      };

      startAutoplay();

      carousel.addEventListener('mouseenter', stopAutoplay);
      carousel.addEventListener('mouseleave', startAutoplay);

      let touchStartX = 0;
      let touchEndX = 0;

      carousel.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
        stopAutoplay();
      }, { passive: true });

      carousel.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        const diff = touchStartX - touchEndX;

        if (Math.abs(diff) > 50) {
          if (diff > 0) {
            goTo((currentIndex + 1) % slides.length);
          } else {
            goTo((currentIndex - 1 + slides.length) % slides.length);
          }
        }
        startAutoplay();
      }, { passive: true });

      const prevBtn = carousel.querySelector('[data-carousel-prev]');
      const nextBtn = carousel.querySelector('[data-carousel-next]');

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          goTo((currentIndex - 1 + slides.length) % slides.length);
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          goTo((currentIndex + 1) % slides.length);
        });
      }
    }
  };

  /* ============================================
   * ADD TO CART FORMS
   * ============================================ */
  const AddToCartForms = {
    init() {
      document.querySelectorAll('[data-add-to-cart-form]').forEach((form) => {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();

          const variantId = form.querySelector('input[name="id"]')?.value;
          const quantity = parseInt(form.querySelector('input[name="quantity"]')?.value, 10) || 1;

          if (!variantId) return;

          const submitBtn = form.querySelector('[data-add-to-cart]');
          if (submitBtn) submitBtn.disabled = true;

          document.dispatchEvent(
            new CustomEvent('cart:add', {
              detail: { id: parseInt(variantId, 10), quantity }
            })
          );

          setTimeout(() => {
            if (submitBtn) submitBtn.disabled = false;
          }, 1000);
        });
      });
    }
  };

  /* ============================================
   * INITIALIZATION
   * ============================================ */
  document.addEventListener('DOMContentLoaded', () => {
    MegaMenu.init();
    CartDrawer.init();
    ScrollAnimations.init();
    StickyHeader.init();
    MobileMenu.init();
    ProductGallery.init();
    VariantSelector.init();
    Accordion.init();
    AnnouncementBar.init();
    NewsletterForm.init();
    TestimonialsCarousel.init();
    AddToCartForms.init();
  });
})();
